import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { MediaLenient } from "@/components/forms/schemas/media";
import { ENRICHMENT_STATUSES } from "@/lib/enrichment";
import {
  movieCastNestedSchema,
  showGenreNestedSchema,
} from "@/pages/movies/schemas/movie.nested.form";

const disabled = fieldConfig({ inputProps: { disabled: true } });

/**
 * Edit schema for a Movie. Binds the full `MovieDbDto` returned by
 * `GET /api/movies/{id}`. On submit the repository maps this back onto the
 * `UpdateMovieCommand` shape (notably `genres` → `genreIds`, and cast entries
 * down to personId|name). `translations` is intentionally omitted from the form
 * so it round-trips untouched — partial updates only send changed fields.
 */
export const movieEditSchema = z.object({
  id: z.string().nullish().superRefine(disabled),
  title: z.string(),
  slug: z.coerce.string().nullish(),
  originalTitle: z.string(),
  originalLanguage: z.string().nullish(),
  releaseDate: z
    .string()
    .datetime({ offset: true })
    .superRefine(fieldConfig({ fieldType: "datetime" })),
  overview: z.string().superRefine(fieldConfig({ fieldType: "textarea" })),
  genres: z
    .array(showGenreNestedSchema)
    .default([])
    .superRefine(fieldConfig({ fieldType: "showGenres" })),
  cast: z
    .array(movieCastNestedSchema)
    .default([])
    .superRefine(fieldConfig({ fieldType: "movieCast" })),
  // Per-country maps keyed by ISO 3166-1 alpha-2 code. Null on movies written
  // before the fields existed. Saved whole: the backend replaces the map.
  certifications: z
    .record(z.string(), z.string())
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "certifications", label: "Age ratings" }),
    ),
  releaseDates: z
    .record(z.string(), z.string())
    .nullish()
    .superRefine(
      fieldConfig({
        fieldType: "releaseDates",
        label: "Release dates by country",
      }),
    ),
  durationInMinutes: z.coerce
    .number()
    .superRefine(fieldConfig({ inputProps: { min: 0 } })),
  popularity: z.coerce.number().nullish().superRefine(disabled),
  adult: z.boolean().default(false),
  status: z.enum(["Draft", "Complete"]),
  tags: z
    .array(z.string())
    .nullish()
    .default([])
    .superRefine(fieldConfig({ fieldType: "tagList" })),
  trailers: z
    .array(
      z.object({
        url: z.string(),
        language: z.string().default("en"),
        subtitles: z.string().nullish(),
      }),
    )
    .nullish()
    .default([]),
  metadata: z
    .object({
      source: z.string().nullish().superRefine(disabled),
      tmdbId: z.coerce.number().nullish().superRefine(disabled),
      imdbId: z.string().nullish(),
      wikiDataId: z.string().nullish(),
    })
    .nullish(),
  // Copy five of the status vocabulary, now derived — see the twin note in
  // `event.edit.form.ts`. `disabled` is dropped with the select: the
  // `enrichmentStatus` field is display-only by construction.
  enrichmentStatus: z
    .enum(ENRICHMENT_STATUSES)
    .nullish()
    .superRefine(fieldConfig({ fieldType: "enrichmentStatus" })),
  media: MediaLenient.superRefine(
    fieldConfig({ fieldType: "media", customData: { collection: "Movies" } }),
  ),
  hide: z.boolean().default(false),
  createdBy: z.string().nullish().superRefine(disabled),
  createdAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "datetime", inputProps: { disabled: true } }),
    ),
  updatedBy: z.string().nullish().superRefine(disabled),
  updatedAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "datetime", inputProps: { disabled: true } }),
    ),
});

export type MovieEditFormType = z.infer<typeof movieEditSchema>;
